<template>
  <div class="convention">
    <div class="card card-box convention-carte">
      <div class="convention-identite">
        <div>
          <h3 class="mb-1">{{ assureur.compagnie }}</h3>
          <span class="convention-pastille">Société à convention directe</span>
        </div>
        <ul class="convention-coordonnees">
          <li v-if="renseigne(assureur.telephone)"><span>Téléphone</span>{{ assureur.telephone }}</li>
          <li v-if="renseigne(assureur.email)"><span>Email</span>{{ assureur.email }}</li>
          <li v-if="renseigne(assureur.adresse)"><span>Adresse</span>{{ assureur.adresse }}</li>
        </ul>
      </div>
    </div>

    <div class="convention-onglets" role="tablist" aria-label="Convention">
      <button v-for="tab in onglets" :key="tab.id" :id="'convention-tab-' + tab.id + '-' + _uid" type="button" role="tab"
        :aria-selected="onglet === tab.id ? 'true' : 'false'" :aria-controls="'convention-panneau-' + _uid"
        :tabindex="onglet === tab.id ? 0 : -1" :class="{ actif: onglet === tab.id }"
        @click="selectionnerOnglet(tab.id)" @keydown="naviguerOnglets($event, tab.id)">{{ tab.libelle }}</button>
    </div>

    <div :id="'convention-panneau-' + _uid" role="tabpanel" :aria-labelledby="'convention-tab-' + onglet + '-' + _uid">
    <div v-if="onglet === 'contrat'" class="card card-box convention-carte">
      <div v-if="erreur && !formulaire" class="alert alert-danger" role="alert">{{ erreur }}</div>
      <div v-if="chargement" class="text-grey">Chargement du contrat…</div>
      <div v-else-if="!contrat" class="convention-vide">
        <p>Aucun contrat. Définissez la période de la convention et ses taux de prise en charge.</p>
        <button type="button" class="btn btn-primary btn-radius" @click="ouvrirContrat">Définir le contrat</button>
      </div>
      <template v-else>
        <div class="convention-entete">
          <div>
            <h4 class="mb-1">Contrat</h4>
            <span class="text-grey">{{ contrat.date_expire_le ? 'Du ' + date(contrat.date_effet) + ' au ' + date(contrat.date_expire_le) : 'Depuis le ' + date(contrat.date_effet) + ', sans date de fin' }}</span>
            <span :class="['badge ml-2', etatContrat.classe]">{{ etatContrat.libelle }}</span>
          </div>
          <div class="convention-actions">
            <button type="button" class="btn btn-primary btn-radius" @click="ouvrirContrat">Modifier le contrat</button>
          </div>
        </div>
        <div class="convention-taux">
          <div v-for="ligne in tauxContrat" :key="ligne.libelle" class="convention-taux-ligne">
            <span>{{ libelleTaux(ligne.libelle) }}</span><strong>{{ pourcent(ligne.taux) }}</strong>
          </div>
          <div v-if="!aTauxSpecialiste" class="convention-taux-ligne text-grey">
            <span>Consultation spécialiste</span><span>comme généraliste</span>
          </div>
        </div>
      </template>
    </div>

    <!-- Facturation du contrat (réglée auparavant dans Caisse › Paramètres › Sociétés), dans sa propre carte : chaque
         réglage, sa valeur et ce qu'il change pour la Caisse et les créances (04/10/2026). -->
    <div v-if="onglet === 'contrat' && contrat && !chargement" class="card card-box convention-carte">
      <div class="convention-entete">
        <div>
          <h4 class="mb-1">Facturation</h4>
          <span class="text-grey">Comment la société est facturée et ce que la Caisse doit saisir.</span>
        </div>
        <button type="button" class="btn btn-secondary-light-o btn-radius" @click="facturationOuverte = true">Modifier la facturation</button>
      </div>
      <p v-if="!facturation" class="text-grey mb-0">Facturation non renseignée.</p>
      <div v-else class="facturation-tableau-scroll">
        <table class="table facturation-tableau">
          <thead><tr><th scope="col">Réglage</th><th scope="col">Valeur</th><th scope="col">Ce que cela change</th></tr></thead>
          <tbody>
            <tr v-for="ligne in lignesFacturation" :key="ligne.reglage">
              <th scope="row">{{ ligne.reglage }}</th>
              <td><span :class="['facturation-valeur', ligne.classe]">{{ ligne.valeur }}</span></td>
              <td class="facturation-effet">{{ ligne.effet }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <ConventionTravailleurs v-if="onglet === 'travailleurs'" :assurance-id="assuranceId" />

    <template v-if="onglet === 'exclusions'">
      <div v-if="chargement" class="card convention-carte" role="status">Chargement du contrat…</div>
      <AssurePrestation v-else-if="contrat" :key="contrat.souscripteur_id" :assurance-id="assuranceId"
        :souscripteur-id="contrat.souscripteur_id" :ouverture="ouvertureExclusions" />
      <div v-else class="card convention-carte"><p>Définissez le contrat avant de choisir ses exclusions.</p>
        <button type="button" class="btn btn-primary btn-radius" @click="ouvrirContrat">Définir le contrat</button></div>
    </template>
    </div>

    <FacturationContrat v-if="facturationOuverte && contrat" :souscripteur-id="contrat.souscripteur_id" :nom="assureur.compagnie"
      @fermer="facturationOuverte = false" @enregistre="facturationEnregistree" />

    <div v-if="formulaire" class="convention-fenetre" role="dialog" aria-modal="true" aria-labelledby="contrat-titre">
      <form class="convention-fenetre-contenu" novalidate @submit.prevent="enregistrer">
        <div class="convention-fenetre-entete">
          <h4 id="contrat-titre" class="mb-0">{{ contrat ? 'Modifier le contrat' : 'Contrat de la convention' }}</h4>
          <button type="button" class="btn btn-link convention-fermer" aria-label="Fermer" @click="fermer">×</button>
        </div>
        <div class="form-row">
          <div class="form-group col-6"><label for="contrat-effet">Date d'effet *</label>
            <input id="contrat-effet" v-model="formulaire.date_effet" type="date" class="input-secondary-light" required></div>
          <div class="form-group col-6"><label for="contrat-expire">Date d'expiration{{ formulaire.sans_fin ? '' : ' *' }}</label>
            <input id="contrat-expire" v-model="formulaire.date_expire" type="date" class="input-secondary-light" :min="formulaire.date_effet"
              :disabled="formulaire.sans_fin" :required="!formulaire.sans_fin">
            <label class="convention-sans-fin"><input v-model="formulaire.sans_fin" type="checkbox" @change="basculerSansFin"> Sans date de fin</label></div>
        </div>
        <div class="convention-taux-commun">
          <div class="form-group mb-0"><label for="contrat-taux-commun">Taux commun</label>
            <div class="input-container"><input id="contrat-taux-commun" v-model.trim="tauxCommun" class="input-secondary-light" inputmode="decimal" placeholder="Ex. 80">
              <span class="input-icon ic-secondary font-wgh-900">%</span></div></div>
          <button type="button" class="btn btn-primary-light btn-radius" :disabled="!tauxCommun" @click="appliquerTauxCommun">Appliquer à toutes les prestations</button>
        </div>
        <p class="text-grey font-13 mb-2">Appliquez un taux commun, puis ajustez les prestations qui ont un taux différent.</p>
        <div class="form-row">
          <div v-for="ligne in formulaire.c_model" :key="ligne.champ_taux" class="form-group col-6">
            <label :for="'contrat-taux-' + ligne.champ_taux">{{ libelleTaux(ligne.champ_taux) }}</label>
            <div class="input-container"><input :id="'contrat-taux-' + ligne.champ_taux" v-model.trim="ligne.valeur" class="input-secondary-light" inputmode="decimal"
              :placeholder="ligne.champ_taux === 'Consultation spécialiste' ? 'Comme généraliste' : ''">
              <span class="input-icon ic-secondary font-wgh-900">%</span></div>
          </div>
        </div>
        <p v-if="erreur" class="text-danger" role="alert">{{ erreur }}</p>
        <div class="text-right">
          <button type="button" class="btn btn-secondary-light-o btn-radius" @click="fermer">Annuler</button>
          <button type="submit" class="btn btn-primary btn-radius ml-2" :disabled="enregistrement">{{ enregistrement ? 'Enregistrement…' : 'Enregistrer' }}</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import ConventionsApi from '@/api/parametres/_conventions'
import ConventionTravailleurs from './convention-travailleurs.vue'
import AssurePrestation from '../liste-assures/assure-prestations'
import FacturationContrat from '../facturation/facturation-contrat.vue'
import { nombre } from '../facturation/facturation-utils'
import { libelleTaux, ordonnerTaux, tauxValide } from '../taux-assurance'

// Société à convention directe (04/10/2026) : pas de sous-société, son contrat unique s'affiche directement.
export default {
  name: 'convention-detail',
  components: { ConventionTravailleurs, FacturationContrat, AssurePrestation },
  props: {
    assureur: { type: Object, required: true },
    assuranceId: { type: [String, Number], required: true },
  },
  data() {
    return { onglet: 'contrat', chargement: true, contrat: null, champs: [], formulaire: null, tauxCommun: '', erreur: '', enregistrement: false,
      facturation: null, facturationOuverte: false, ouvertureExclusions: 0,
      onglets: [{ id: 'contrat', libelle: 'Contrat et taux' }, { id: 'travailleurs', libelle: 'Travailleurs et familles' }, { id: 'exclusions', libelle: 'Exclusions' }] }
  },
  computed: {
    // Facturation lisible : réglage, valeur, effet ; montant et durée seulement pour un forfait.
    lignesFacturation() {
      const f = this.facturation
      if (!f) return []
      const forfait = Number(f.a_model_forfait) === 1
      const lignes = [{ reglage: 'Mode de facturation', valeur: forfait ? 'Forfait' : 'À l’acte', classe: forfait ? 'facturation-forfait' : 'facturation-acte',
        effet: forfait
          ? 'Les patients de la société ne paient rien à la Caisse. Leurs consommations sont suivies dans Créances › Consommations réelles et facturées dans Factures forfaitaires.'
          : 'Chaque prestation est facturée à la société selon les taux du contrat, dans Créances › Factures assurance.' }]
      if (forfait) {
        lignes.push({ reglage: 'Montant du forfait', valeur: this.nombre(f.prix_unitaire_forfait) + ' × ' + this.nombre(f.qte_forfait) + ' = ' + this.nombre(f.montant_forfait) + ' ' + this.monnaie,
          classe: '', effet: 'Prix unitaire × quantité : le montant facturé à la société.' })
        lignes.push({ reglage: 'Durée de couverture', valeur: f.duree_forfait ? f.duree_forfait + ' jour(s)' : 'Non renseignée', classe: f.duree_forfait ? '' : 'facturation-manque',
          effet: 'Durée d’hospitalisation couverte ; au-delà, les « exclusions après durée » s’appliquent.' })
      }
      lignes.push({ reglage: 'N° de bon de la société', valeur: this.bonObligatoire ? 'Obligatoire' : 'Facultatif', classe: '',
        effet: this.bonObligatoire ? 'La Caisse saisit le n° de bon délivré par la société et sa date d’expiration.' : 'La Caisse peut encaisser sans n° de bon.' })
      lignes.push({ reglage: 'Préfixe des factures', valeur: f.prefixe_reference_facture || 'Non renseigné', classe: f.prefixe_reference_facture ? '' : 'facturation-manque',
        effet: 'Début de la référence des factures adressées à la société.' })
      return lignes
    },
    bonObligatoire() {
      const bon = this.facturation ? this.facturation.num_bon_is_required : null
      return bon === null || bon === undefined || Number(bon) === 1
    },
    monnaie() {
      return this.currency_info && this.currency_info.principal_currency ? this.currency_info.principal_currency : ''
    },
    tauxContrat() {
      return this.contrat ? ordonnerTaux(this.contrat.prestation || [], 'libelle') : []
    },
    aTauxSpecialiste() {
      return this.tauxContrat.some(ligne => ligne.libelle === 'Consultation spécialiste')
    },
    etatContrat() {
      if (this.contrat && !this.contrat.date_expire_le) return { libelle: 'Valide · sans date de fin', classe: 'badge-success' }
      const jours = this.contrat ? Number(this.contrat.validite) : -1
      if (jours < 0) return { libelle: 'Expiré', classe: 'badge-danger' }
      if (jours <= 30) return { libelle: 'Expire dans ' + jours + ' j', classe: 'badge-warning' }
      return { libelle: 'Valide', classe: 'badge-success' }
    },
  },
  mounted() {
    this.charger()
  },
  methods: {
    selectionnerOnglet(id) {
      this.onglet = id
      if (id === 'exclusions') this.ouvertureExclusions++
    },
    naviguerOnglets(event, id) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
      event.preventDefault()
      const index = this.onglets.findIndex(tab => tab.id === id)
      const suivant = event.key === 'Home' ? 0 : event.key === 'End' ? this.onglets.length - 1
        : (index + (event.key === 'ArrowRight' ? 1 : -1) + this.onglets.length) % this.onglets.length
      // Déplacer le focus sans ouvrir de modale : Entrée/Espace active l'onglet.
      const boutons = this.$el.querySelectorAll('.convention-onglets [role="tab"]')
      if (boutons[suivant]) boutons[suivant].focus()
    },
    libelleTaux,
    renseigne(valeur) {
      return valeur && valeur !== '-'
    },
    date(valeur) {
      return valeur ? new Date(valeur + 'T00:00:00').toLocaleDateString('fr-FR') : '—'
    },
    pourcent(taux) {
      if (taux === null || taux === undefined || taux === '') return '—'
      return Math.round(Number(taux) * 1000) / 10 + ' %'
    },
    charger() {
      this.chargement = true
      return Promise.all([ConventionsApi.contrats(this.assuranceId), ConventionsApi.champsTaux(this.assuranceId), ConventionsApi.souscripteurs(this.assuranceId)]).then(reponses => {
        const contrats = Array.isArray(reponses[0]) ? reponses[0] : []
        // Un contrat encore sans taux n'apparaît pas dans la liste des taux : on le reprend de la liste des souscripteurs.
        const souscripteurs = Array.isArray(reponses[2]) ? reponses[2] : []
        const sansTaux = souscripteurs.length ? { souscripteur_id: souscripteurs[0].id, date_effet: souscripteurs[0].date_effet,
          date_expire_le: souscripteurs[0].date_expire_le, validite: souscripteurs[0].validite, prestation: [] } : null
        this.contrat = contrats.length ? contrats[0] : sansTaux
        this.champs = Array.isArray(reponses[1]) ? reponses[1] : []
        return this.chargerFacturation()
      }).catch(() => {
        this.erreur = 'Le contrat n’a pas pu être chargé.'
      }).then(() => {
        this.chargement = false
      })
    },
    nombre,
    chargerFacturation() {
      if (!this.contrat) {
        this.facturation = null
        return
      }
      return ConventionsApi.infoSociete(this.contrat.souscripteur_id).then(reponse => {
        this.facturation = reponse && reponse.data ? reponse.data : null
      }).catch(() => {
        this.facturation = null
      })
    },
    facturationEnregistree() {
      this.facturationOuverte = false
      return this.chargerFacturation()
    },
    ouvrirContrat() {
      const existants = {}
      ;(this.contrat ? this.contrat.prestation || [] : []).forEach(ligne => { existants[ligne.libelle] = ligne.taux })
      const libelles = []
      this.champs.forEach(champ => { if (libelles.indexOf(champ.champ_taux) < 0) libelles.push(champ.champ_taux) })
      Object.keys(existants).forEach(libelle => { if (libelles.indexOf(libelle) < 0) libelles.push(libelle) })
      const enPourcent = taux => (taux === undefined || taux === null || taux === '') ? '' : String(Math.round(Number(taux) * 100))
      this.formulaire = {
        date_effet: this.contrat ? this.contrat.date_effet : '',
        date_expire: this.contrat ? this.contrat.date_expire_le || '' : '',
        // Contrat sans date de fin : la date d'expiration reste vide.
        sans_fin: !!this.contrat && !this.contrat.date_expire_le,
        c_model: ordonnerTaux(libelles.map(libelle => ({ champ_id: 0, champ_taux: libelle, valeur: enPourcent(existants[libelle]) })), 'champ_taux'),
      }
      this.tauxCommun = ''
      this.erreur = ''
    },
    basculerSansFin() {
      if (this.formulaire.sans_fin) this.formulaire.date_expire = ''
    },
    fermer() {
      this.formulaire = null
      this.erreur = ''
    },
    appliquerTauxCommun() {
      if (!tauxValide(this.tauxCommun)) {
        this.erreur = 'Le taux commun doit être compris entre 0 et 100 %.'
        return
      }
      this.erreur = ''
      this.formulaire.c_model.forEach(ligne => { ligne.valeur = this.tauxCommun })
    },
    utilisateurId() {
      const info = this.$store ? this.$store.getters['authInfo'] : null
      return info ? info.id : 0
    },
    enregistrer() {
      const f = this.formulaire
      if (!f.date_effet || (!f.sans_fin && !f.date_expire)) {
        this.erreur = f.sans_fin ? 'Renseignez la date d’effet.' : 'Renseignez la date d’effet et la date d’expiration, ou cochez « Sans date de fin ».'
        return
      }
      if (!f.sans_fin && f.date_expire < f.date_effet) {
        this.erreur = 'La date d’expiration doit suivre la date d’effet.'
        return
      }
      if (!f.c_model.every(ligne => tauxValide(ligne.valeur))) {
        this.erreur = 'Chaque taux doit être compris entre 0 et 100 %.'
        return
      }
      this.enregistrement = true
      this.erreur = ''
      return ConventionsApi.enregistrerContrat({
        user_id: this.utilisateurId(),
        is_edit: this.contrat ? this.contrat.souscripteur_id : 0,
        assurance_id: this.assuranceId,
        societe: this.assureur.compagnie,
        date_effet: f.date_effet,
        date_expire: f.sans_fin ? '' : f.date_expire,
        c_model: f.c_model.map(ligne => ({ champ_id: ligne.champ_id, champ_taux: ligne.champ_taux, valeur: String(ligne.valeur).replace(',', '.') })),
      }).then(reponse => {
        if (reponse && reponse.success) {
          this.formulaire = null
          if (this.$toasted) this.$toasted.show('Contrat enregistré', { duration: 3000, theme: 'toasted-primary', type: 'success', position: 'top-center' })
          return this.charger()
        }
        this.erreur = reponse && reponse.code === 423 ? 'Cette convention a déjà un contrat : rechargez la page.' : 'Le contrat n’a pas pu être enregistré.'
      }).catch(() => {
        this.erreur = 'Le contrat n’a pas pu être enregistré.'
      }).then(() => {
        this.enregistrement = false
      })
    },
  },
}
</script>

<style scoped>
.convention-carte { padding: 24px; margin-bottom: 20px; border-radius: 18px }
.convention-identite { display: flex; justify-content: space-between; gap: 24px; flex-wrap: wrap }
.convention-pastille { display: inline-block; padding: 3px 10px; border-radius: 999px; background: #e5f1ff; color: #0075ff; font-size: 13px; font-weight: 600 }
.convention-coordonnees { list-style: none; margin: 0; padding: 0; display: grid; gap: 4px }
.convention-coordonnees span { display: inline-block; min-width: 90px; color: var(--ui-text-secondary, #65728b) }
.convention-onglets { display: flex; gap: 4px; margin-bottom: 20px; background: #fff; border-radius: 14px; padding: 0 14px; overflow-x: auto; border-bottom: 1px solid #dce4f2 }
.convention-onglets button { flex: 0 0 auto; border: 0; border-bottom: 3px solid transparent; background: transparent; color: var(--ui-text-secondary, #273449); border-radius: 0; padding: 16px 20px 13px; font-size: 14px; font-weight: 600; white-space: nowrap }
.convention-onglets button.actif { background: transparent; border-bottom-color: #4f6cf6; color: var(--ui-accent, #2457c5) }
.convention-onglets button:focus-visible { outline: 2px solid #2457c5; outline-offset: -4px }
.convention-entete { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 16px; flex-wrap: wrap }
.convention-actions { display: flex; gap: 8px; flex-wrap: wrap }
.facturation-tableau-scroll { overflow-x: auto }
.facturation-tableau { margin: 0 }
.facturation-tableau thead th { border-top: 0; color: var(--ui-text-secondary, #65728b); font-size: 13px; font-weight: 600; text-transform: none }
.facturation-tableau th[scope="row"] { color: #22324f; font-weight: 600; white-space: nowrap; width: 1% }
.facturation-tableau td { vertical-align: middle }
.facturation-valeur { display: inline-block; font-weight: 700; color: #22324f; white-space: nowrap }
.facturation-forfait, .facturation-acte { padding: 3px 12px; border-radius: 999px; font-size: 14px }
.facturation-forfait { background: #fff1e0; color: #b25e09 }
.facturation-acte { background: #e7efff; color: #2457c5 }
.facturation-manque { color: #b42318; font-weight: 600 }
.facturation-effet { color: var(--ui-text-secondary, #65728b); font-size: 14px; min-width: 260px }
.convention-taux { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 8px 24px }
.convention-taux-ligne { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #edf0f6 }
.convention-vide { text-align: center; padding: 24px 0 }
.convention-taux-commun { display: flex; align-items: flex-end; gap: 12px; padding: 12px; margin-bottom: 6px; background: #f2f8ff; border-radius: 12px; flex-wrap: wrap }
.convention-taux-commun .form-group { width: 160px }
.convention-fenetre { position: fixed; z-index: 1050; inset: 0; display: flex; align-items: center; justify-content: center; background: rgba(30, 40, 64, .45); padding: 16px }
.convention-fenetre-contenu { background: #fff; border-radius: 16px; width: 100%; max-width: 760px; max-height: 90vh; overflow-y: auto; padding: 24px }
.convention-fenetre-contenu .form-group label { display: block }
.convention-sans-fin { display: inline-flex !important; align-items: center; gap: 6px; margin-top: 8px; font-size: 14px }
.convention-fenetre-entete { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 16px }
.convention-fenetre-entete h4 { font-size: 22px; line-height: 1.3 }
.convention-fermer { font-size: 24px; line-height: 1 }
</style>
